import { apiPrefix } from 'src/settings';
import { logError, logInfo } from 'src/loggingManager';
import { parseError } from 'src/util/misc';
import { makeAuthenticatedServiceRequest, makePublicServiceRequest } from './servicesClient';
import { createAsset, createVersion, updateAsset, getAsset, getVersion } from './assetsClient';
import { SceneService_ImportAssetType, SceneType_NotSaved } from 'src/types/scene';
import { TransformTypes } from 'src/types/transforms';
import {
    AssetType,
    SceneService_AssetType,
    SceneService_AssetType_NotSaved,
    SceneService_AssetType_Version,
} from 'src/types/asset';

const SCENE_ENDPOINT = `https://${apiPrefix}scenes.documents.cimpress.io`;

type UpdateSceneParams = {
    tenantType: string;
    tenantId: string;
    sceneId: string;
    scene: Partial<SceneType_NotSaved>;
};

type InputSceneParams = Omit<UpdateSceneParams, 'sceneId'> & {
    scene: SceneType_NotSaved;
};

function mapSceneToAsset(scene: SceneType_NotSaved): Omit<SceneService_AssetType_NotSaved, 'tenant'> {
    const initialRevision = scene.initialVersion && {
        uri: scene.initialVersion.indexUri,
        metadata: {
            transformations: scene.initialVersion.transformations,
        },
    };
    return {
        name: scene.name,
        description: scene.description,
        notes: scene.notes,
        referenceId: scene.mcpSku,
        referenceIdVersion: scene.mcpSkuVersion ? +scene.mcpSkuVersion : 0,
        attributes: scene.attributes,
        assetType: 'scene',
        tags: scene.tags,
        latestRevisionId: scene.latestVersionId,
        publishedRevisionId: scene.publishedVersionId,
        deleted: !!scene.deleted,
        initialRevision,
    };
}

function mapPartialSceneToAsset(scene: Partial<SceneType_NotSaved>) {
    const initialRevision = scene.initialVersion && {
        uri: scene.initialVersion.indexUri,
        metadata: {
            transformations: scene.initialVersion.transformations,
        },
    };
    return {
        ...(scene.name !== undefined && { name: scene.name }),
        ...(scene.description !== undefined && { description: scene.description }),
        ...(scene.notes !== undefined && { notes: scene.notes }),
        ...(scene.mcpSku !== undefined && { referenceId: scene.mcpSku }),
        ...{ referenceIdVersion: scene.mcpSkuVersion !== undefined ? +scene.mcpSkuVersion : 0 },
        ...(scene.attributes !== undefined && { attributes: scene.attributes }),
        ...(scene.tags !== undefined && { tags: scene.tags }),
        ...(scene.latestVersionId !== undefined && { latestRevisionId: scene.latestVersionId }),
        ...(scene.publishedVersionId !== undefined && { publishedRevisionId: scene.publishedVersionId }),
        ...(scene.deleted !== undefined && { deleted: scene.deleted }),
        ...(initialRevision && { initialRevision }),
        assetType: 'scene',
    };
}

/**
 * Creates a scene.
 * @param options
 * @param options.tenantType - The type of owner resource (merchant / fulfiller).
 * @param options.tenantId - The id of the resource.
 * @param options.scene - The object containing all scene properties.
 * @param options.scene.name - Name of the scene.
 * @param options.scene.description - Description of the scene.
 * @param options.scene.notes - Notes of the scene.
 * @param options.scene.tags - Optional array of tags for the scene.
 * @param options.scene.mcpSku - Optional sku associated with the scene for searching.
 * @param options.scene.mcpSkuVersion - Optional sku version associated with the scene for searching.
 * @param options.scene.initialVersion - The optional initial version for the scene.
 * @param options.scene.initialVersion.indexUri - The link to the initial scene uri.
 * @param options.scene.initialVersion.transformations - Optional transformation data if this scene contains cylinders.
 * @returns - The promise of the request.
 */
export function createScene({ tenantType, tenantId, scene }: InputSceneParams) {
    const asset = mapSceneToAsset(scene);
    asset.initialRevision = {
        uri: scene.initialVersion.indexUri,
        metadata: {
            transformations: scene.initialVersion.transformations,
        },
    };

    return createAsset({ asset, tenantType, tenantId }).then((newScene) => {
        logInfo(`Created scene: ${newScene} ${JSON.stringify(newScene || {})}`);
        return newScene;
    });
}

type CreateSceneVersionParams = {
    sceneId: string;
    version: {
        indexUri: string;
        transformations?: TransformTypes[];
    };
};

/** Creates a scene version.
 * @param options
 * @param options.sceneId - The id of the scene.
 * @param options.version - The scene version object.
 * @param options.version.indexUri - The link to the new scene uri.
 * @param options.version.transformations - Optional transformation data if this scene contains cylinders.
 * @returns - The promise of the request.
 */
export function createSceneVersion({ sceneId, version }: CreateSceneVersionParams) {
    const mappedVersion = {
        uri: version.indexUri,
        metadata: {
            transformations: version.transformations,
        },
    };

    return createVersion({ assetId: sceneId, version: mappedVersion });
}

/**
 * Gets a scene.
 * @param options
 * @param options.sceneId - The id of the scene.
 * @returns - The promise of the request.
 */
export function getScene({ sceneId }: { sceneId: string }) {
    return getAsset({ assetId: sceneId }).then((asset) =>
        getVersion({ assetId: sceneId, versionId: asset.latestVersionId || '' }).then((version) => {
            if (Object.keys(version.metadata).length > 0 || Array.isArray(version.metadata)) {
                // eslint-disable-next-line no-param-reassign
                asset.cylinderWarp = version.metadata.transformations
                    ? version.metadata.transformations
                    : version.metadata;
            }
            return asset;
        }),
    );
}

/**
 * Updates a scene.
 * @param options
 * @param options.tenantType - The type of owner resource (merchant / fulfiller).
 * @param options.tenantId - The id of the resource.
 * @param options.sceneId - The id of the scene.
 * @param options.scene - The updated scene object.
 * @param options.scene.name - The updated scene name.
 * @param options.scene.mcpSku - The updated sku.
 * @param options.scene.description - The updated scene description.
 * @param options.scene.latestVersionId - The updated latest version.
 * @param options.scene.publishedVersionId - The updated published version.
 * @param options.scene.tags - string array of tags for the scene
 * @param options.scene.deleted - if the string is deleted or not
 * @returns - The promise of the request.
 */
export function updateScene({ tenantType, tenantId, sceneId, scene }: UpdateSceneParams) {
    const asset = mapPartialSceneToAsset(scene);
    return updateAsset({ tenantType, tenantId, assetId: sceneId, newAsset: asset });
}

function mapImportedSceneResponse(
    response: { data: SceneService_ImportAssetType } | void,
): SceneService_AssetType | undefined {
    if (response && response.data) {
        const scene = response.data;
        return {
            ...scene,
            publishedRevisionId: scene.publishedCompositionId,
            recentPublishedRevisions: scene.recentPublishedCompositions,
            referenceId: scene.mcpSku,
            latestRevisionId: scene.latestCompositionId,
        };
    }
}

// Helper to parse an asset
export function parseScene(asset: SceneService_AssetType | undefined): AssetType | undefined {
    if (!asset) return undefined;

    return {
        latestVersionId: asset.latestRevisionId,
        publishedVersionId: asset.publishedRevisionId,
        id: asset.id,
        name: asset.name,
        mcpSku: undefined,
        description: undefined,
        created: asset.created,
        owner: asset.owner,
        lastModified: asset.lastModified,
        modifiedBy: asset.modifiedBy,
        lastPublished: asset.lastPublished,
        publishedBy: asset.publishedBy,
        variables: {},
        tenantType: asset.tenant.type,
        tenantId: asset.tenant.id,
        deleted: asset.deleted || false,
        tags: [],
        publishedVersions: (asset.recentPublishedRevisions || []).map((version: SceneService_AssetType_Version) => ({
            id: version.compositionId,
            date: version.publishedAt,
            publisher: version.publishedBy,
        })),
    };
}

/**
 * Imports scene.
 * @param options
 * @param options.zip - Zip File containing the scene.
 * @returns - The promise of the request.
 */
export function importScene({ zip, tenantType, tenantId }: { zip: File; tenantType: string; tenantId: string }) {
    const url = `${SCENE_ENDPOINT}/v2/${tenantType}/${tenantId}/sceneimport`;
    const data = new FormData();
    data.append('file', zip, zip.name);
    return makeAuthenticatedServiceRequest({
        url,
        method: 'post',
        data,
        header: {
            // Reset default `Content-Type` header to `undefined` to allow Axios to set the correct `Content-Type` header
            'Content-Type': undefined,
        },
    })
        .then(mapImportedSceneResponse)
        .then(parseScene)
        .catch((e) => {
            logError(`Failure to import scene! ${parseError(e)}`);
            throw e;
        });
}

/**
 * Clean scene texture (uv map) image
 * @param options
 * @param options.texture - texture image file
 * @returns The promise of the request.
 */
export function cleanTexture({ texture }: { texture: File }) {
    const url = `${SCENE_ENDPOINT}/v3/scenes/texture:clean`;
    const data = new FormData();
    data.append('file', texture, texture.name);

    return makePublicServiceRequest({
        url,
        method: 'post',
        data,
        responseType: 'blob', // Changed from 'arraybuffer' to 'blob' for fetch compatibility
        header: {
            // Reset default `Content-Type` header to `undefined` to allow fetch to set the correct `Content-Type` header
            'Content-Type': undefined,
        },
    })
        .then((cleanedTexture) => new File([cleanedTexture.data], texture.name, { type: 'image/png' }))
        .catch((e) => {
            logError(`Failure to clean texture image! ${parseError(e)}`);
            throw e;
        });
}

/**
 * get texture (uv map) image
 * @param options
 * @param options.width - width of identity texture image
 * @param options.height - height of identity texture image file
 * @returns The promise of the request.
 */
export function getTextureIdentity({ width, height }: { width: number; height: number }) {
    const url = `${SCENE_ENDPOINT}/v3/scenes/texture/identity:generate?width=${width}&height=${height}`;

    return makePublicServiceRequest({ url, method: 'get', responseType: 'blob' })
        .then((identityTexture) => new File([identityTexture.data], 'identityTexture', { type: 'image/png' }))
        .catch((e) => {
            logError(`Failure to get identity texture image of ${width}x${height} size! ${parseError(e)}`);
            throw e;
        });
}
